import {authorizeGovernedSourceReference} from "@/lib/authorizeGovernedSourceReference";
import type {BiRpcClient} from "@/lib/governedBiRetrieval";
import { supabase } from "@/integrations/supabase/client";
import type { CoachEvidenceItem } from "@/lib/coachEvidenceResolver";

export type CoachEvidenceAuthorization =
  | { status: "authorized" }
  | { status: "revoked" }
  | { status: "unavailable" }
  | { status: "retryable_error" };

type RelationshipAuthorization = {
  authorized: boolean;
  relationshipRevision: string | null;
  status?: "authorized" | "revoked" | "retryable_error";
};

type SourceQueryResult = Promise<{ data: unknown; error: unknown }>;

interface CoachEvidenceSourceFilterBuilder {
  eq(column: string, value: string): CoachEvidenceSourceFilterBuilder;
  maybeSingle(): SourceQueryResult;
}

interface CoachEvidenceSourceQueryBuilder {
  select(columns: string): CoachEvidenceSourceFilterBuilder;
}

export interface CoachEvidenceSourceClient {
  rpc?: BiRpcClient["rpc"];
  from(table: string): CoachEvidenceSourceQueryBuilder;
}

export type CoachEvidenceAuthorizationItem = Pick<CoachEvidenceItem, "canonicalIdentity">;

export type AuthorizeCoachEvidenceReferenceParams<T extends CoachEvidenceAuthorizationItem> = {
  coachId: string;
  leaderId: string;
  relationshipRevision: string | null;
  resolvedItem: T;
  expectedSourceRevision?: string;
  expectedAccessGeneration: string;
  getCurrentAccessGeneration: () => string;
  reauthorizeRelationship: () => Promise<RelationshipAuthorization>;
  client?: CoachEvidenceSourceClient;
};

function sourceQuery(
  client: CoachEvidenceSourceClient,
  item: CoachEvidenceAuthorizationItem,
  coachId: string,
  leaderId: string,
  expectedSourceRevision?: string,
): CoachEvidenceSourceFilterBuilder {
  const { sourceType, sourceId } = item.canonicalIdentity;
  switch (sourceType) {
    case "transcript_upload":
      return client.from("transcript_uploads")
        .select("id")
        .eq("id", sourceId)
        .eq("leader_id", leaderId);
    case "post_meeting_reflection":
      return client.from("post_meeting_reflections")
        .select(expectedSourceRevision ? "id,source_revision::text" : "id")
        .eq("id", sourceId)
        .eq("leader_id", leaderId);
    case "assessment":
      return client.from("assessments")
        .select("id, leader_behaviors!inner(leader_id)")
        .eq("id", sourceId)
        .eq("leader_behaviors.leader_id", leaderId);
    case "shared_reflection":
      return client.from("shared_reflections")
        .select(expectedSourceRevision ? "id,source_revision::text" : "id")
        .eq("id", sourceId)
        .eq("leader_id", leaderId);
    case "leader_artifact":
      return client.from("leader_artifacts")
        .select(expectedSourceRevision ? "id,source_revision::text" : "id")
        .eq("id", sourceId)
        .eq("leader_id", leaderId);
    case "practice_focus_suggestion":
      return client.from("practice_focus_suggestions")
        .select("id")
        .eq("id", sourceId)
        .eq("leader_id", leaderId)
        .eq("coach_id", coachId);
  }
}

export async function authorizeCoachEvidenceReference<T extends CoachEvidenceAuthorizationItem>({
  coachId,
  leaderId,
  relationshipRevision,
  resolvedItem,
  expectedSourceRevision,
  expectedAccessGeneration,
  getCurrentAccessGeneration,
  reauthorizeRelationship,
  client = supabase as unknown as CoachEvidenceSourceClient,
}: AuthorizeCoachEvidenceReferenceParams<T>): Promise<CoachEvidenceAuthorization> {
  if (
    !coachId
    || !leaderId
    || !relationshipRevision
    || resolvedItem.canonicalIdentity.leaderId !== leaderId
  ) {
    return { status: "revoked" };
  }

  try {
    const relationship = await reauthorizeRelationship();
    if (relationship.status === "retryable_error") {
      return { status: "retryable_error" };
    }
    if (
      !relationship.authorized
      || relationship.relationshipRevision !== relationshipRevision
      || getCurrentAccessGeneration() !== expectedAccessGeneration
    ) {
      return { status: "revoked" };
    }

    const { data, error } = await sourceQuery(client, resolvedItem, coachId, leaderId, expectedSourceRevision).maybeSingle();
    if (error) return { status: "retryable_error" };
    if (!data) return { status: "unavailable" };
    if (expectedSourceRevision) {
      if (resolvedItem.canonicalIdentity.sourceType === "transcript_upload") {
        if (!client.rpc) return {status:"retryable_error"};
        const current=await authorizeGovernedSourceReference({client:{rpc:client.rpc.bind(client)},leaderId,audience:"coach",sourceType:"transcript_upload",sourceId:resolvedItem.canonicalIdentity.sourceId,revision:expectedSourceRevision});
        if (current.status !== "authorized") return current;
      } else if (typeof data!=="object" || !("source_revision" in data) || String(data.source_revision)!==expectedSourceRevision) return {status:"unavailable"};
    }
    if (getCurrentAccessGeneration() !== expectedAccessGeneration) return { status: "revoked" };

    return { status: "authorized" };
  } catch {
    return { status: "retryable_error" };
  }
}
